import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Beaker, Sparkles, Send, Trophy, Clock, Users, Mail, BarChart3,
  TrendingUp, AlertCircle, Zap, RefreshCw, Copy, ChevronDown, ChevronRight, Eye,
  Calendar as CalendarIcon, Repeat, XCircle, CheckCircle2, Flame, Cpu, Play, Edit3,
} from 'lucide-react';
import { EmailPreviewModal } from './EmailPreviewModal';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLE_OPTIONS = [
  { id: 'punchy',       label: 'Punchy' },
  { id: 'urgent',       label: 'Urgent' },
  { id: 'friendly',     label: 'Friendly' },
  { id: 'data-driven',  label: 'Data-Driven' },
];

const SECTIONS = [
  { id: 'create',    label: 'A/B Auto-Winner',    icon: Beaker },
  { id: 'optimizer', label: 'Auto-Optimizer',     icon: Cpu },
  { id: 'best-time', label: 'Best Time to Send',  icon: Flame },
  { id: 'scheduled', label: 'Scheduled Jobs',     icon: CalendarIcon },
  { id: 'history',   label: 'Campaign History',   icon: BarChart3 },
  { id: 'segments',  label: 'Carrier Segments',   icon: Users },
  { id: 'audit',     label: 'Audit Log',          icon: Trophy, adminOnly: true },
];

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const EmailLabTab = () => {
  const { user } = useAuth();
  const [section, setSection]                 = useState('create');
  const [recommendedSlot, setRecommendedSlot] = useState(null);
  const isAdmin = user?.role === 'admin';
  const visibleSections = SECTIONS.filter((s) => !s.adminOnly || isAdmin);

  return (
    <div className="space-y-6" data-testid="email-lab-tab">
      {/* Section tabs */}
      <div className="flex gap-2 border-b border-zinc-200 flex-wrap">
        {visibleSections.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            data-testid={`email-lab-section-${s.id}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wide border-b-2 transition-colors ${
              section === s.id
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-900'
            }`}
          >
            <s.icon className="w-4 h-4" />
            {s.label}
          </button>
        ))}
      </div>

      {section === 'create'    && <CreateABCampaign recommendedSlot={recommendedSlot} />}
      {section === 'optimizer' && <AutoOptimizerPanel />}
      {section === 'best-time' && <BestTimePanel onUseSlot={(slot) => { setRecommendedSlot(slot); setSection('create'); }} />}
      {section === 'scheduled' && <ScheduledJobsPanel />}
      {section === 'history'   && <ABCampaignHistory />}
      {section === 'segments'  && <CarrierSegments />}
      {section === 'audit'     && isAdmin && <AuditLogPanel />}
    </div>
  );
};

// ===================================================================
// A/B Auto-Winner Creator
// ===================================================================

const VARIABLE_HINT = 'Variables: {load_count} {top_rate} {top_lane} {top_origin_state}';

const CreateABCampaign = ({ recommendedSlot }) => {
  const [templates, setTemplates] = useState({ subjects: [], ctas: [] });
  const [regions, setRegions] = useState([]);
  const [carrierType, setCarrierType] = useState('OTR');
  const [regionIds, setRegionIds] = useState([]);

  const [subjectA, setSubjectA] = useState('');
  const [ctaA, setCtaA] = useState('🔍 See all {load_count} loads');
  const [subjectB, setSubjectB] = useState('');
  const [ctaB, setCtaB] = useState('📞 Book this load now');

  const [testPercent, setTestPercent] = useState(20);
  const [waitMinutes, setWaitMinutes] = useState(60);

  // Phase 2 — schedule & resend
  const [scheduleOn, setScheduleOn]         = useState(false);
  const [scheduleAt, setScheduleAt]         = useState(''); // datetime-local value
  const [resendOn, setResendOn]             = useState(false);
  const [resendHours, setResendHours]       = useState(48);
  const [resendSubject, setResendSubject]   = useState('');

  const [aiStyle, setAiStyle] = useState('punchy');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiTarget, setAiTarget] = useState('A');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/email/subject-templates`)
      .then((r) => setTemplates(r.data))
      .catch(() => {});
    axios.get(`${API}/regions`)
      .then((r) => setRegions(r.data))
      .catch(() => {});
  }, []);

  // If Best-Time panel handed us a slot, pre-fill the schedule.
  useEffect(() => {
    if (recommendedSlot) {
      const next = nextDatetimeForSlot(recommendedSlot.dow, recommendedSlot.hour);
      setScheduleOn(true);
      setScheduleAt(toDatetimeLocal(next));
    }
  }, [recommendedSlot]);

  const filteredRegions = regions.filter((r) =>
    carrierType === 'OTR' ? r.region_type === 'STATE' : (r.region_type === 'PORT' || r.region_type === 'RAIL')
  );

  const toggleRegion = (id) =>
    setRegionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSuggest = async () => {
    setAiLoading(true);
    setAiSuggestions([]);
    try {
      const res = await axios.post(`${API}/email/suggest-subjects`, { n: 4, style: aiStyle });
      setAiSuggestions(res.data?.suggestions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI suggestion failed');
    } finally {
      setAiLoading(false);
    }
  };

  const pickSubject = (text) => {
    if (aiTarget === 'A') setSubjectA(text);
    else setSubjectB(text);
    toast.success(`Subject applied to Variant ${aiTarget}`);
  };

  const handleSend = async () => {
    if (!subjectA.trim() || !subjectB.trim()) {
      toast.error('Both subject variants are required');
      return;
    }
    let scheduledIso = null;
    if (scheduleOn) {
      if (!scheduleAt) {
        toast.error('Pick a date/time or turn off Schedule');
        return;
      }
      const d = new Date(scheduleAt);
      if (isNaN(d.getTime())) {
        toast.error('Invalid schedule datetime');
        return;
      }
      if (d.getTime() < Date.now() + 60_000) {
        toast.error('Schedule must be at least 1 minute in the future');
        return;
      }
      scheduledIso = d.toISOString();
    }

    setSending(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/email/send-digest-auto-winner`, {
        carrier_type: carrierType,
        region_ids: regionIds,
        variant_a_subject: subjectA,
        variant_a_cta_label: ctaA,
        variant_b_subject: subjectB,
        variant_b_cta_label: ctaB,
        test_percent: Number(testPercent),
        wait_minutes: Number(waitMinutes),
        scheduled_at: scheduledIso,
        resend_to_non_openers: resendOn,
        resend_wait_hours: Number(resendHours) || 48,
        resend_subject: resendSubject.trim() || null,
      });
      setLastResult(res.data);
      if (res.data.scheduled) {
        toast.success(`Campaign queued for ${new Date(res.data.scheduled_at).toLocaleString()}`);
      } else {
        toast.success(`A/B test started · ${res.data.test_recipients} carriers in test pool${res.data.resend_job ? ' · resend queued' : ''}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start A/B campaign');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* LEFT — Campaign form */}
      <div className="lg:col-span-2 space-y-6">
        {/* Audience */}
        <Card title="Audience" icon={Users}>
          <div className="flex gap-2 mb-3">
            <PillButton active={carrierType === 'OTR'}    onClick={() => { setCarrierType('OTR'); setRegionIds([]); }} testId="el-type-otr">OTR</PillButton>
            <PillButton active={carrierType === 'DRAYAGE'} onClick={() => { setCarrierType('DRAYAGE'); setRegionIds([]); }} testId="el-type-drayage">Drayage</PillButton>
          </div>
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
            {carrierType === 'OTR' ? 'States (optional)' : 'Ports/Ramps (optional)'}
          </div>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {filteredRegions.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => toggleRegion(r.id)}
                className={`px-3 py-1.5 text-xs font-bold border transition-colors ${
                  regionIds.includes(r.id)
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                }`}
              >
                {r.name}
              </button>
            ))}
          </div>
          {regionIds.length === 0 && (
            <p className="text-xs text-zinc-500 mt-2">Empty = all active {carrierType} carriers.</p>
          )}
        </Card>

        {/* Variant A */}
        <VariantEditor
          label="A"
          subject={subjectA}
          setSubject={setSubjectA}
          cta={ctaA}
          setCta={setCtaA}
          ctaTemplates={templates.ctas}
          subjectTemplates={templates.subjects}
          onAiFocus={() => setAiTarget('A')}
        />

        {/* Variant B */}
        <VariantEditor
          label="B"
          subject={subjectB}
          setSubject={setSubjectB}
          cta={ctaB}
          setCta={setCtaB}
          ctaTemplates={templates.ctas}
          subjectTemplates={templates.subjects}
          onAiFocus={() => setAiTarget('B')}
        />

        {/* Settings */}
        <Card title="Auto-Winner Settings" icon={Trophy}>
          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="Test pool %"
              hint="Percentage of list to use for the A/B test"
              value={testPercent}
              min={5}
              max={50}
              onChange={setTestPercent}
              testId="el-test-percent"
            />
            <NumberField
              label="Wait minutes"
              hint="Time before winner is sent to remainder"
              value={waitMinutes}
              min={5}
              max={1440}
              onChange={setWaitMinutes}
              testId="el-wait-minutes"
            />
          </div>
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800 flex gap-2">
            <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>How it works:</strong> {testPercent}% of carriers will be split 50/50 between A and B.
              After {waitMinutes} minutes, the winner (highest click-rate, tie-broken by open-rate) is sent
              to the remaining {100 - Number(testPercent)}%.
            </div>
          </div>
        </Card>

        {/* Phase 2 — Schedule for later */}
        <Card title="Schedule for Later (optional)" icon={CalendarIcon}
              action={
                <ToggleButton on={scheduleOn} onToggle={() => setScheduleOn((v) => !v)} testId="el-schedule-toggle" />
              }>
          {scheduleOn ? (
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500">Send at</label>
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                data-testid="el-schedule-datetime"
                className="w-full px-3 py-2 border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
              />
              <p className="text-[11px] text-zinc-500">
                Campaign is queued in <code className="bg-zinc-100 px-1">scheduled_email_jobs</code> and fires automatically at the chosen time.
              </p>
              {recommendedSlot && (
                <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
                  Using recommended slot from Best-Time analytics: <strong>{DOW_LABELS[recommendedSlot.dow]} at {String(recommendedSlot.hour).padStart(2, '0')}:00</strong>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-zinc-500">Toggle on to schedule this campaign for a future time.</p>
          )}
        </Card>

        {/* Phase 2 — Auto-resend to non-openers */}
        <Card title="Auto-Resend to Non-Openers (optional)" icon={Repeat}
              action={
                <ToggleButton on={resendOn} onToggle={() => setResendOn((v) => !v)} testId="el-resend-toggle" />
              }>
          {resendOn ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                label="Wait hours"
                hint="How long after the initial send"
                value={resendHours}
                min={1}
                max={336}
                onChange={setResendHours}
                testId="el-resend-hours"
              />
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Resend subject (optional)</label>
                <input
                  type="text"
                  value={resendSubject}
                  onChange={(e) => setResendSubject(e.target.value)}
                  placeholder="⚡ Still available — <original>"
                  className="w-full px-3 py-2 border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
                  data-testid="el-resend-subject"
                />
                <p className="text-[10px] text-zinc-400 mt-1">Leave blank to use the default.</p>
              </div>
              <div className="md:col-span-2 bg-blue-50 border border-blue-200 text-[11px] text-blue-800 p-3">
                We&apos;ll find every recipient who never opened the initial A/B send and email
                them again after <strong>{resendHours} hours</strong>. Openers are skipped so
                no one gets the same message twice.
              </div>
            </div>
          ) : (
            <p className="text-xs text-zinc-500">Toggle on to automatically re-engage carriers who didn&apos;t open the first send.</p>
          )}
        </Card>

        <button
          onClick={handleSend}
          disabled={sending}
          data-testid="el-send-auto-winner-btn"
          className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-4 font-black uppercase tracking-wide disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
          {sending
            ? (scheduleOn ? 'Scheduling…' : 'Starting A/B test…')
            : (scheduleOn ? 'Schedule Auto-Winner Campaign' : 'Start Auto-Winner Campaign')}
        </button>

        {lastResult && <ResultPanel result={lastResult} />}
      </div>

      {/* RIGHT — AI Sidebar */}
      <div className="space-y-6">
        <Card title="AI Subject Suggestions" icon={Sparkles}>
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Style</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {STYLE_OPTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setAiStyle(s.id)}
                data-testid={`el-style-${s.id}`}
                className={`px-3 py-1.5 text-xs font-bold border transition-colors ${
                  aiStyle === s.id
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Apply to</div>
          <div className="flex gap-2 mb-3">
            <PillButton active={aiTarget === 'A'} onClick={() => setAiTarget('A')} testId="el-aitarget-a">Variant A</PillButton>
            <PillButton active={aiTarget === 'B'} onClick={() => setAiTarget('B')} testId="el-aitarget-b">Variant B</PillButton>
          </div>
          <button
            onClick={handleSuggest}
            disabled={aiLoading}
            data-testid="el-ai-generate-btn"
            className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white py-3 font-bold uppercase tracking-wide text-sm disabled:opacity-50"
          >
            {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {aiLoading ? 'Generating…' : 'Generate 4 ideas'}
          </button>

          {aiSuggestions.length > 0 && (
            <div className="mt-4 space-y-2">
              {aiSuggestions.map((line, i) => (
                <button
                  key={i}
                  onClick={() => pickSubject(line)}
                  className="w-full text-left px-3 py-2 border border-zinc-200 hover:border-violet-400 hover:bg-violet-50 transition-colors text-sm flex items-start gap-2"
                  data-testid={`el-ai-suggestion-${i}`}
                >
                  <Copy className="w-3.5 h-3.5 text-violet-500 flex-shrink-0 mt-0.5" />
                  <span className="text-zinc-700">{line}</span>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card title="Subject Library" icon={Mail}>
          <p className="text-xs text-zinc-500 mb-3">Click to copy into the focused variant.</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {templates.subjects.map((t) => (
              <button
                key={t.id}
                onClick={() => pickSubject(t.text)}
                className="w-full text-left px-3 py-2 border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 transition-colors text-xs"
              >
                <div className="font-bold text-[10px] uppercase tracking-wider text-zinc-400">{t.category}</div>
                <div className="text-zinc-700 mt-0.5">{t.text}</div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

// ===================================================================
// Reusable sub-components
// ===================================================================

const Card = ({ title, icon: Icon, children, action }) => (
  <div className="bg-white border border-zinc-200">
    <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-zinc-500" />}
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const PillButton = ({ active, children, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${
      active ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
    }`}
  >
    {children}
  </button>
);

const NumberField = ({ label, hint, value, min, max, onChange, testId }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full px-3 py-2 border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
    />
    {hint && <p className="text-xs text-zinc-400 mt-1">{hint}</p>}
  </div>
);

const VariantEditor = ({ label, subject, setSubject, cta, setCta, ctaTemplates, onAiFocus }) => {
  const isA = label === 'A';
  return (
    <Card
      title={`Variant ${label}`}
      icon={isA ? Mail : Mail}
      action={
        <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${isA ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
          {label}
        </span>
      }
    >
      <div className="space-y-3" onFocus={onAiFocus} onClick={onAiFocus}>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid={`el-subject-${label.toLowerCase()}`}
            placeholder={`e.g., ${isA ? '🚨 {load_count} fresh loads — first to call wins' : '💵 Top load pays {top_rate}'}`}
            className="w-full px-3 py-2 border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
          />
          <p className="text-[11px] text-zinc-400 mt-1">{VARIABLE_HINT}</p>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">CTA Label</label>
          <input
            type="text"
            value={cta}
            onChange={(e) => setCta(e.target.value)}
            data-testid={`el-cta-${label.toLowerCase()}`}
            className="w-full px-3 py-2 border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
          />
          {ctaTemplates?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {ctaTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setCta(t.label)}
                  className="text-[11px] px-2 py-1 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

const ResultPanel = ({ result }) => {
  const scheduled = new Date(result.scheduled_at);
  return (
    <Card title="A/B Test Started" icon={Trophy}>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Stat label="Test pool" value={result.test_recipients} />
        <Stat label="Remainder" value={result.remainder_recipients} />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <VariantSummary label="A" data={result.variant_a} accent="blue" />
        <VariantSummary label="B" data={result.variant_b} accent="orange" />
      </div>
      <div className="text-xs text-zinc-500 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" />
        Winner sends at <span className="font-bold text-zinc-900">{scheduled.toLocaleString()}</span> · ab_group <code className="bg-zinc-100 px-1.5 py-0.5">{result.ab_group_id}</code>
      </div>
    </Card>
  );
};

const VariantSummary = ({ label, data, accent }) => (
  <div className={`border-l-4 ${accent === 'blue' ? 'border-blue-500' : 'border-orange-500'} bg-zinc-50 p-3`}>
    <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Variant {label} · {data?.recipients || 0} sent</div>
    <div className="text-sm text-zinc-900 mt-1 line-clamp-2">{data?.subject}</div>
    <div className="text-[11px] text-zinc-500 mt-1">{data?.cta}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="bg-zinc-50 border border-zinc-200 p-3">
    <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-2xl font-black text-zinc-900 mt-1">{value}</div>
  </div>
);

// ===================================================================
// A/B Campaign History
// ===================================================================

const ABCampaignHistory = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [plans, setPlans] = useState({});
  const [previewCampaignId, setPreviewCampaignId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email/ab-campaigns`);
      setCampaigns(res.data?.campaigns || []);
    } catch {
      toast.error('Failed to load A/B history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!plans[id]) {
      try {
        const res = await axios.get(`${API}/email/ab-plan/${id}`);
        setPlans((p) => ({ ...p, [id]: res.data }));
      } catch {
        setPlans((p) => ({ ...p, [id]: null }));
      }
    }
  };

  const triggerNow = async (id) => {
    if (!window.confirm('Send the winner to the remainder right now?')) return;
    try {
      await axios.post(`${API}/email/ab-winner-now/${id}`);
      toast.success('Winner-send executed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }

  if (!campaigns.length) {
    return (
      <div className="text-center py-16 text-zinc-500" data-testid="el-history-empty">
        <BarChart3 className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
        No A/B campaigns yet. Run your first auto-winner above.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="el-history-list">
      {previewCampaignId && (
        <EmailPreviewModal campaignId={previewCampaignId} onClose={() => setPreviewCampaignId(null)} />
      )}
      {campaigns.map((c) => {
        const open = expanded === c.ab_group_id;
        const plan = plans[c.ab_group_id];
        return (
          <div key={c.ab_group_id} className="bg-white border border-zinc-200">
            <button
              onClick={() => toggle(c.ab_group_id)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-zinc-50"
              data-testid={`el-history-row-${c.ab_group_id}`}
            >
              <div className="flex items-center gap-3 text-left">
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <div>
                  <div className="font-bold text-sm text-zinc-900">{c.ab_group_id}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {new Date(c.latest_created_at).toLocaleString()} · {c.variants.length} variant{c.variants.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              {c.winner && (
                <span className="flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 text-[11px] font-black uppercase tracking-wider">
                  <Trophy className="w-3 h-3" /> Winner: {c.winner}
                </span>
              )}
            </button>
            {open && (
              <div className="border-t border-zinc-200 p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {c.variants.map((v, i) => (
                    <div
                      key={i}
                      className={`border ${v.variant === c.winner && !v.is_winner_send ? 'border-amber-400 bg-amber-50' : 'border-zinc-200'} p-3`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500">
                          Variant {v.variant} {v.is_winner_send && '· Winner Send'}
                        </span>
                        <div className="flex items-center gap-2">
                          {v.variant === c.winner && !v.is_winner_send && (
                            <Trophy className="w-3.5 h-3.5 text-amber-500" />
                          )}
                          {v.campaign_id && (
                            <button
                              onClick={() => setPreviewCampaignId(v.campaign_id)}
                              data-testid={`el-history-view-${v.campaign_id}`}
                              title="View sent email"
                              className="flex items-center gap-1 text-[10px] font-bold text-zinc-500 hover:text-red-600"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-zinc-900 line-clamp-2">{v.subject}</div>
                      <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                        <MiniStat label="Sent"     value={v.sent} />
                        <MiniStat label="Opens"    value={v.opened}  rate={v.open_rate} />
                        <MiniStat label="Clicks"   value={v.clicked} rate={v.click_rate} />
                        <MiniStat label="Bounced"  value={v.delivered ? (v.sent - v.delivered) : 0} />
                      </div>
                    </div>
                  ))}
                </div>

                {plan && (
                  <div className="bg-zinc-50 border border-zinc-200 p-3 text-xs flex items-center justify-between">
                    <div className="flex items-center gap-2 text-zinc-700">
                      <Clock className="w-3.5 h-3.5" />
                      Plan status: <strong>{plan.status}</strong>
                      {plan.scheduled_at && <> · scheduled {new Date(plan.scheduled_at).toLocaleString()}</>}
                      {plan.winner && <> · winner: {plan.winner}</>}
                    </div>
                    {plan.status === 'scheduled' && (
                      <button
                        onClick={() => triggerNow(c.ab_group_id)}
                        data-testid={`el-trigger-now-${c.ab_group_id}`}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide"
                      >
                        Send Winner Now
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MiniStat = ({ label, value, rate }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{label}</div>
    <div className="text-sm font-bold text-zinc-900">{value ?? 0}</div>
    {rate !== undefined && <div className="text-[10px] text-zinc-500">{rate}%</div>}
  </div>
);

// ===================================================================
// Carrier Segments
// ===================================================================

const SEGMENT_META = {
  vip:     { label: 'VIP',     color: 'bg-amber-100 text-amber-800 border-amber-300',   icon: Trophy },
  active:  { label: 'Active',  color: 'bg-green-100 text-green-800 border-green-300',   icon: TrendingUp },
  cold:    { label: 'Cold',    color: 'bg-blue-100 text-blue-800 border-blue-300',      icon: AlertCircle },
  bounced: { label: 'Bounced', color: 'bg-red-100 text-red-800 border-red-300',         icon: AlertCircle },
};

const CarrierSegments = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    axios.get(`${API}/carriers/segments`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load segments'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="el-segments">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(SEGMENT_META).map(([key, meta]) => {
          const M = meta.icon;
          return (
            <div key={key} className={`border-2 ${meta.color} p-4`}>
              <div className="flex items-center justify-between">
                <M className="w-4 h-4" />
                <span className="text-[10px] font-black uppercase tracking-wider">{meta.label}</span>
              </div>
              <div className="text-3xl font-black mt-2">{data.counts?.[key] ?? 0}</div>
              <div className="text-[11px] mt-1 opacity-75">{data.definitions?.[key]}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.keys(SEGMENT_META).map((key) => {
          const examples = data.examples?.[key] || [];
          return (
            <Card key={key} title={`${SEGMENT_META[key].label} carriers`} icon={SEGMENT_META[key].icon}>
              {examples.length === 0 ? (
                <p className="text-xs text-zinc-500">No carriers in this segment yet.</p>
              ) : (
                <ul className="space-y-2">
                  {examples.map((e) => (
                    <li key={e.email} className="flex items-center justify-between text-xs border-b border-zinc-100 pb-2">
                      <button
                        onClick={() => setSelectedEmail(e.email)}
                        className="text-zinc-700 hover:text-red-600 hover:underline truncate text-left"
                        data-testid={`el-segment-${key}-${e.email}`}
                      >
                        {e.email}
                      </button>
                      <div className="flex items-center gap-2 text-zinc-500 flex-shrink-0 ml-2">
                        <span title="opens">{e.opens}o</span>
                        <span title="clicks">{e.clicks}c</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          );
        })}
      </div>

      {selectedEmail && (
        <CarrierProfileModal email={selectedEmail} onClose={() => setSelectedEmail(null)} />
      )}
    </div>
  );
};

const CarrierProfileModal = ({ email, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/carriers/${encodeURIComponent(email)}/interactions`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load carrier history'))
      .finally(() => setLoading(false));
  }, [email]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="el-carrier-profile-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-zinc-500" />
            <h3 className="font-black text-sm uppercase tracking-wider">{email}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-xl leading-none">×</button>
        </div>

        {loading || !data ? (
          <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-4 gap-3">
              <Stat label="Sent"     value={data.summary.total} />
              <Stat label="Opened"   value={`${data.summary.opened} (${data.summary.open_rate}%)`} />
              <Stat label="Clicked"  value={`${data.summary.clicked} (${data.summary.click_rate}%)`} />
              <Stat label="Bounced"  value={data.summary.bounced} />
            </div>

            <div>
              <h4 className="text-xs font-black uppercase tracking-wider text-zinc-500 mb-3">Recent emails</h4>
              <div className="space-y-2">
                {(data.logs || []).slice(0, 30).map((log, i) => (
                  <div key={i} className="border border-zinc-200 p-3 text-xs">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-zinc-900 truncate flex-1">{log.subject || log.email_type}</div>
                      <div className="text-zinc-500 ml-2 flex-shrink-0">
                        {log.created_at ? new Date(log.created_at).toLocaleDateString() : ''}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-1.5">
                      {log.delivered && <Badge color="green">Delivered</Badge>}
                      {log.opened    && <Badge color="blue">Opened</Badge>}
                      {log.clicked   && <Badge color="violet">Clicked</Badge>}
                      {log.bounced   && <Badge color="red">Bounced</Badge>}
                      {log.variant_label && <Badge color="zinc">Variant {log.variant_label}</Badge>}
                    </div>
                  </div>
                ))}
                {(!data.logs || data.logs.length === 0) && (
                  <p className="text-xs text-zinc-500">No email history yet.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Badge = ({ color, children }) => {
  const cls = {
    green:  'bg-green-100 text-green-700',
    blue:   'bg-blue-100 text-blue-700',
    violet: 'bg-violet-100 text-violet-700',
    red:    'bg-red-100 text-red-700',
    zinc:   'bg-zinc-100 text-zinc-700',
  }[color] || 'bg-zinc-100 text-zinc-700';
  return <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{children}</span>;
};


// ===================================================================
// Phase 2 — Toggle button
// ===================================================================

const ToggleButton = ({ on, onToggle, testId }) => (
  <button
    onClick={onToggle}
    data-testid={testId}
    className={`relative w-11 h-6 transition-colors ${on ? 'bg-red-600' : 'bg-zinc-300'}`}
    aria-pressed={on}
  >
    <span className={`absolute top-0.5 w-5 h-5 bg-white transition-all ${on ? 'left-5' : 'left-0.5'}`} />
  </button>
);

// ===================================================================
// Phase 2 — Best-Time-to-Send heatmap
// ===================================================================

const BestTimePanel = ({ onUseSlot }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/email-lab/best-time`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load best-time analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }
  if (!data) return null;

  if (!data.has_enough_data) {
    return (
      <div className="bg-white border border-zinc-200 p-8 text-center" data-testid="el-best-time-empty">
        <Flame className="w-8 h-8 mx-auto mb-3 text-zinc-300" />
        <div className="text-sm text-zinc-700 font-bold mb-1">Not enough data yet</div>
        <p className="text-xs text-zinc-500 max-w-md mx-auto">
          Send at least 20 tracked emails before we can recommend the best send times.
          Currently tracked: <strong>{data.total_sent}</strong>.
        </p>
      </div>
    );
  }

  // Build a color scale on open_rate
  const cellColor = (rate) => {
    if (rate === null || rate === undefined) return 'bg-zinc-100';
    if (rate >= 40) return 'bg-emerald-600 text-white';
    if (rate >= 25) return 'bg-emerald-400 text-emerald-950';
    if (rate >= 15) return 'bg-amber-300 text-amber-900';
    if (rate >= 5)  return 'bg-amber-100 text-amber-900';
    return 'bg-zinc-200 text-zinc-500';
  };

  return (
    <div className="space-y-6" data-testid="el-best-time">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatMini label="Emails tracked"    value={data.total_sent} />
        <StatMini label="Opens"             value={data.total_opened} />
        <StatMini label="Overall open rate" value={`${data.overall_open_rate}%`} />
      </div>

      {/* Top slots */}
      <div className="bg-white border border-zinc-200 p-5">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-3">Top 5 recommended send times</h3>
        {data.top_slots.length === 0 ? (
          <p className="text-xs text-zinc-500">No slot has enough opens yet — check back after more sends.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            {data.top_slots.map((slot, i) => (
              <button
                key={i}
                onClick={() => onUseSlot(slot)}
                data-testid={`el-best-time-slot-${i}`}
                className="text-left border border-zinc-200 hover:border-red-500 hover:bg-red-50 p-3 transition-colors"
              >
                <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400">Rank #{i + 1}</div>
                <div className="text-lg font-black text-zinc-900 mt-0.5">{DOW_LABELS[slot.dow]}</div>
                <div className="text-2xl font-black text-red-600">{String(slot.hour).padStart(2, '0')}:00</div>
                <div className="text-[11px] text-zinc-500 mt-1">{slot.open_rate}% open · {slot.sent} sent</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Heatmap */}
      <div className="bg-white border border-zinc-200 p-5">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-3">
          Open-rate heatmap · {data.tz}
        </h3>
        <div className="overflow-x-auto">
          <table className="text-[10px]">
            <thead>
              <tr>
                <th className="w-8"></th>
                {Array.from({ length: 24 }).map((_, h) => (
                  <th key={h} className="px-1 py-1 text-zinc-400 font-bold">{String(h).padStart(2, '0')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DOW_LABELS.map((label, dow) => (
                <tr key={dow}>
                  <td className="pr-2 text-zinc-500 font-bold text-right">{label}</td>
                  {Array.from({ length: 24 }).map((_, hour) => {
                    const cell = data.heatmap.find((h) => h.dow === dow && h.hour === hour);
                    const rate = cell?.open_rate;
                    return (
                      <td
                        key={hour}
                        title={cell ? `${DOW_LABELS[dow]} ${hour}:00 · ${cell.sent} sent, ${cell.opened} opens${rate !== null ? `, ${rate}% rate` : ''}` : ''}
                        className={`w-6 h-6 text-center align-middle text-[10px] font-bold ${cellColor(rate)}`}
                      >
                        {rate !== null && rate !== undefined ? Math.round(rate) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-3 mt-3 text-[10px] text-zinc-500">
          <LegendSwatch color="bg-zinc-200" label="< 5%" />
          <LegendSwatch color="bg-amber-100" label="5–14%" />
          <LegendSwatch color="bg-amber-300" label="15–24%" />
          <LegendSwatch color="bg-emerald-400" label="25–39%" />
          <LegendSwatch color="bg-emerald-600" label="40%+" />
          <span>Cells with fewer than 5 sends are hidden.</span>
        </div>
      </div>
    </div>
  );
};

const StatMini = ({ label, value }) => (
  <div className="bg-white border border-zinc-200 p-4">
    <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400">{label}</div>
    <div className="text-3xl font-black text-zinc-900 mt-1">{value}</div>
  </div>
);

const LegendSwatch = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className={`inline-block w-3 h-3 ${color}`} />
    {label}
  </span>
);

// ===================================================================
// Phase 2 — Scheduled Jobs list
// ===================================================================

const STATUS_META = {
  pending:   { color: 'bg-amber-100 text-amber-800',     icon: Clock,        label: 'Pending' },
  running:   { color: 'bg-blue-100 text-blue-800',       icon: RefreshCw,    label: 'Running' },
  completed: { color: 'bg-green-100 text-green-800',     icon: CheckCircle2, label: 'Completed' },
  failed:    { color: 'bg-red-100 text-red-800',         icon: AlertCircle,  label: 'Failed' },
  cancelled: { color: 'bg-zinc-200 text-zinc-600',       icon: XCircle,      label: 'Cancelled' },
};

const ScheduledJobsPanel = () => {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingJob, setEditingJob] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-lab/scheduled-jobs`);
      setJobs(res.data?.jobs || []);
    } catch {
      toast.error('Failed to load scheduled jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this scheduled job?')) return;
    try {
      await axios.post(`${API}/email-lab/scheduled-jobs/${id}/cancel`);
      toast.success('Job cancelled');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }
  if (!jobs.length) {
    return (
      <div className="bg-white border border-zinc-200 p-10 text-center" data-testid="el-scheduled-empty">
        <CalendarIcon className="w-8 h-8 mx-auto mb-3 text-zinc-300" />
        <div className="text-sm font-bold text-zinc-700 mb-1">No scheduled jobs</div>
        <p className="text-xs text-zinc-500">Schedule an A/B campaign or turn on auto-resend to see queued jobs here.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200" data-testid="el-scheduled-list">
      {editingJob && (
        <ScheduledJobEditModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSaved={() => { setEditingJob(null); refresh(); }}
        />
      )}
      <table className="w-full text-xs">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-left">
            <th className="px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Job</th>
            <th className="px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Type</th>
            <th className="px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Scheduled at</th>
            <th className="px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Status</th>
            <th className="px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500 w-24">Actions</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => {
            const meta = STATUS_META[j.status] || STATUS_META.pending;
            const M = meta.icon;
            return (
              <tr key={j.id} className="border-t border-zinc-100" data-testid={`el-scheduled-row-${j.id}`}>
                <td className="px-4 py-3 font-mono text-[11px] text-zinc-700">{j.id}</td>
                <td className="px-4 py-3 text-zinc-800">{j.type.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 text-zinc-700">{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 ${meta.color} px-2 py-1 text-[10px] font-black uppercase tracking-wider`}>
                    <M className="w-3 h-3" />
                    {meta.label}
                  </span>
                  {j.error && <div className="text-[10px] text-red-700 mt-1 max-w-xs truncate" title={j.error}>{j.error}</div>}
                </td>
                <td className="px-4 py-3">
                  {j.status === 'pending' && (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setEditingJob(j)}
                        data-testid={`el-scheduled-edit-${j.id}`}
                        className="text-[11px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => cancel(j.id)}
                        data-testid={`el-scheduled-cancel-${j.id}`}
                        className="text-[11px] font-bold text-red-600 hover:text-red-800"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ===================================================================
// Phase 2 — Helpers
// ===================================================================

// Compute the next occurrence of a given weekday (0=Mon..6=Sun) at a given hour, in the local browser tz.
function nextDatetimeForSlot(dow, hour) {
  // JS Date.getDay(): 0=Sun..6=Sat  →  convert: dow (0=Mon..6=Sun)
  const jsTargetDay = (dow + 1) % 7; // 0=Mon(1), … 6=Sun(0)
  const now = new Date();
  const result = new Date(now);
  result.setHours(hour, 0, 0, 0);
  let daysAhead = (jsTargetDay - now.getDay() + 7) % 7;
  if (daysAhead === 0 && result <= now) daysAhead = 7;
  result.setDate(now.getDate() + daysAhead);
  return result;
}

function toDatetimeLocal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ===================================================================
// Phase 2b — Auto-Optimizer
// ===================================================================

const CARRIER_TYPES = ['OTR', 'DRAYAGE'];

const AutoOptimizerPanel = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(null);
  const [planning, setPlanning] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, lb] = await Promise.all([
        axios.get(`${API}/email-lab/optimizer`),
        axios.get(`${API}/email-lab/style-leaderboard`).catch(() => ({ data: null })),
      ]);
      setData(cfg.data);
      setLeaderboard(lb.data);
    } catch {
      toast.error('Failed to load optimizer configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveConfig = async (ct, next) => {
    setSaving(ct);
    try {
      await axios.put(`${API}/email-lab/optimizer/${ct}`, next);
      toast.success(`${ct} optimizer ${next.enabled ? 'enabled' : 'saved'}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const planNow = async () => {
    setPlanning(true);
    try {
      const res = await axios.post(`${API}/email-lab/optimizer/plan-now`);
      const n = res.data?.planned_count || 0;
      if (n > 0) {
        toast.success(`Planned ${n} campaign${n > 1 ? 's' : ''}`);
      } else {
        toast.info('Nothing new to plan — check skipped reasons in server logs');
      }
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to plan');
    } finally {
      setPlanning(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="el-optimizer">
      {/* Explainer */}
      <div className="bg-zinc-900 text-white p-5">
        <div className="flex items-center gap-2 mb-2">
          <Cpu className="w-4 h-4 text-red-400" />
          <span className="text-[10px] font-black uppercase tracking-[0.18em] text-red-400">Set-and-Forget A/B</span>
        </div>
        <h3 className="text-xl font-black mb-2">Auto-Optimizer</h3>
        <p className="text-sm text-zinc-300 leading-relaxed max-w-3xl">
          Once enabled per carrier type, the optimizer runs daily to schedule new A/B campaigns
          at each week&apos;s highest open-rate slots (from your Best-Time analytics), with fresh
          AI-generated subject lines and auto-resend to non-openers baked in.
        </p>
        <button
          onClick={planNow}
          disabled={planning}
          className="mt-4 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-50"
          data-testid="el-optimizer-plan-now"
        >
          {planning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          {planning ? 'Planning…' : 'Plan Next Campaigns Now'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {CARRIER_TYPES.map((ct) => (
          <OptimizerConfigCard
            key={ct}
            initial={data.configs[ct]}
            availableStyles={data.available_styles}
            onSave={(next) => saveConfig(ct, next)}
            saving={saving === ct}
          />
        ))}
      </div>

      {/* Style Leaderboard */}
      {leaderboard && (
        <div className="bg-white border border-zinc-200 p-5" data-testid="el-style-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-500" />
              Winner Learning · Style Leaderboard
            </h3>
            <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold">Last {leaderboard.days} days</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CARRIER_TYPES.map((ct) => (
              <div key={ct} className="border border-zinc-200">
                <div className="bg-zinc-50 px-3 py-2 border-b border-zinc-200 text-[10px] font-black uppercase tracking-wider text-zinc-500">
                  {ct}
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50">
                    <tr className="text-left">
                      <th className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-zinc-400">Style</th>
                      <th className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-zinc-400 text-right">Wins</th>
                      <th className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-zinc-400 text-right">Losses</th>
                      <th className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-zinc-400 text-right">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(leaderboard.leaderboards?.[ct] || []).map((row, i) => (
                      <tr key={row.style} className="border-t border-zinc-100" data-testid={`el-leaderboard-${ct}-${row.style}`}>
                        <td className="px-3 py-2">
                          <span className="font-bold text-zinc-900 capitalize">{row.style}</span>
                          {i === 0 && row.matches > 0 && (
                            <span className="ml-1 text-[9px] font-black text-amber-600">★</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-green-700 font-bold">{row.wins}</td>
                        <td className="px-3 py-2 text-right text-red-600 font-bold">{row.losses}</td>
                        <td className="px-3 py-2 text-right font-mono text-zinc-700">{(row.weight * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          {(!leaderboard.history || leaderboard.history.length === 0) ? (
            <p className="text-[11px] text-zinc-500 mt-3">
              No completed A/B duels yet — weights are evenly distributed. After each Auto-Optimizer campaign finishes, the winning style earns a durable weight boost for future variant generation.
            </p>
          ) : (
            <div className="mt-4">
              <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-2">
                Recent matches ({leaderboard.history.length})
              </div>
              <div className="space-y-1">
                {leaderboard.history.slice(0, 6).map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] bg-zinc-50 px-2 py-1.5 border border-zinc-100">
                    <span className="text-[9px] font-black text-zinc-400 uppercase w-16">{h.carrier_type}</span>
                    <span className="font-bold text-green-700 capitalize">{h.winner_style || '—'}</span>
                    <span className="text-zinc-400">beat</span>
                    <span className="font-bold text-zinc-500 capitalize">{h.loser_style || '—'}</span>
                    <span className="text-[10px] text-zinc-400 ml-auto">
                      {h.winner_click_rate != null && `${h.winner_click_rate}% ctr`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upcoming preview */}
      <div className="bg-white border border-zinc-200 p-5">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-3">
          Upcoming optimizer-planned campaigns · {data.upcoming?.length || 0}
        </h3>
        {(!data.upcoming || data.upcoming.length === 0) ? (
          <p className="text-xs text-zinc-500" data-testid="el-optimizer-upcoming-empty">
            No auto-planned campaigns yet. Enable an optimizer above and hit “Plan Next Campaigns Now”.
          </p>
        ) : (
          <div className="space-y-2">
            {data.upcoming.map((j) => {
              const p = j.payload || {};
              const slot = p.optimizer_slot || {};
              return (
                <div key={j.id} className="border border-zinc-200 p-3 bg-zinc-50" data-testid={`el-optimizer-upcoming-${j.id}`}>
                  <div className="flex justify-between items-start gap-3 mb-1.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-red-600">
                      {p.carrier_type} · {DOW_LABELS[slot.dow] ?? '?'} {String(slot.hour ?? '').padStart(2, '0')}:00
                    </span>
                    <span className="text-[10px] text-zinc-500">
                      {j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : ''}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-800"><strong>A ({slot.style_a}):</strong> {p.variant_a_subject}</div>
                  <div className="text-xs text-zinc-800 mt-1"><strong>B ({slot.style_b}):</strong> {p.variant_b_subject}</div>
                  {slot.expected_open_rate !== undefined && (
                    <div className="text-[10px] text-emerald-700 mt-1.5">
                      Historical open rate for this slot: <strong>{slot.expected_open_rate}%</strong>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const OptimizerConfigCard = ({ initial, availableStyles, onSave, saving }) => {
  const [enabled, setEnabled]     = useState(initial.enabled);
  const [freq, setFreq]           = useState(initial.frequency_per_week);
  const [styles, setStyles]       = useState(initial.style_pool || []);
  const [resendOn, setResendOn]   = useState(initial.resend_to_non_openers);
  const [resendHrs, setResendHrs] = useState(initial.resend_wait_hours);
  const [testPct, setTestPct]     = useState(initial.test_percent);
  const [waitMin, setWaitMin]     = useState(initial.wait_minutes);

  useEffect(() => {
    setEnabled(initial.enabled);
    setFreq(initial.frequency_per_week);
    setStyles(initial.style_pool || []);
    setResendOn(initial.resend_to_non_openers);
    setResendHrs(initial.resend_wait_hours);
    setTestPct(initial.test_percent);
    setWaitMin(initial.wait_minutes);
  }, [initial]);

  const toggleStyle = (s) =>
    setStyles((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = () => onSave({
    enabled, region_ids: initial.region_ids || [],
    frequency_per_week: Number(freq) || 2,
    style_pool: styles.length ? styles : availableStyles,
    test_percent: Number(testPct) || 20,
    wait_minutes: Number(waitMin) || 60,
    resend_to_non_openers: resendOn,
    resend_wait_hours: Number(resendHrs) || 48,
  });

  const ct = initial.carrier_type;
  return (
    <div className={`bg-white border-2 ${enabled ? 'border-red-500' : 'border-zinc-200'} p-5`} data-testid={`el-optimizer-card-${ct}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-400">Carrier type</div>
          <h4 className="text-2xl font-black text-zinc-900">{ct}</h4>
        </div>
        <ToggleButton on={enabled} onToggle={() => setEnabled((v) => !v)} testId={`el-optimizer-toggle-${ct}`} />
      </div>

      <div className={enabled ? '' : 'opacity-60 pointer-events-none'}>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <NumberField label="Campaigns / week" hint="1–7" value={freq} min={1} max={7}
                       onChange={setFreq} testId={`el-optimizer-freq-${ct}`} />
          <NumberField label="Test pool %" hint="A/B initial split" value={testPct} min={5} max={50}
                       onChange={setTestPct} testId={`el-optimizer-testpct-${ct}`} />
        </div>

        <div className="mb-3">
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Style pool</label>
          <div className="flex flex-wrap gap-2">
            {availableStyles.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleStyle(s)}
                data-testid={`el-optimizer-style-${ct}-${s}`}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide border transition-colors ${
                  styles.includes(s)
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'border-zinc-300 text-zinc-600 hover:border-zinc-400'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-zinc-200 pt-3 mt-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Auto-resend to non-openers</div>
            <ToggleButton on={resendOn} onToggle={() => setResendOn((v) => !v)} testId={`el-optimizer-resend-${ct}`} />
          </div>
          {resendOn && (
            <NumberField label="Resend wait hours" hint="After initial send" value={resendHrs}
                         min={1} max={336} onChange={setResendHrs} testId={`el-optimizer-resend-hrs-${ct}`} />
          )}
        </div>
      </div>

      <button
        onClick={submit}
        disabled={saving}
        data-testid={`el-optimizer-save-${ct}`}
        className="w-full mt-4 flex items-center justify-center gap-2 bg-zinc-900 hover:bg-red-600 text-white py-2.5 text-xs font-black uppercase tracking-wider transition-colors disabled:opacity-50"
      >
        {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};


// ===================================================================
// Scheduled Job — Edit modal (admin-only edit of pending campaigns)
// ===================================================================

const ScheduledJobEditModal = ({ job, onClose, onSaved }) => {
  const { user } = useAuth();
  const p = job.payload || {};
  const [subjectA, setSubjectA]     = useState(p.variant_a_subject || '');
  const [ctaA,     setCtaA]         = useState(p.variant_a_cta_label || '');
  const [subjectB, setSubjectB]     = useState(p.variant_b_subject || '');
  const [ctaB,     setCtaB]         = useState(p.variant_b_cta_label || '');
  const [testPct,  setTestPct]      = useState(p.test_percent ?? 20);
  const [waitMin,  setWaitMin]      = useState(p.wait_minutes ?? 60);
  const [scheduledAt, setScheduledAt] = useState(() => {
    if (!job.scheduled_at) return '';
    const d = new Date(job.scheduled_at);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [resendOn, setResendOn]     = useState(!!p.resend_to_non_openers);
  const [resendHrs, setResendHrs]   = useState(p.resend_wait_hours ?? 48);
  const [resendSubj, setResendSubj] = useState(p.resend_subject || '');
  const [saving, setSaving]         = useState(false);
  const [testEmail, setTestEmail]   = useState(user?.email || '');
  const [testVariant, setTestVariant] = useState('A');
  const [testSending, setTestSending] = useState(false);

  const sendTest = async () => {
    const email = (testEmail || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    const subj = testVariant === 'A' ? subjectA : subjectB;
    if (!subj.trim()) {
      toast.error(`Variant ${testVariant} subject is empty — fill it in first`);
      return;
    }
    setTestSending(true);
    try {
      const res = await axios.post(
        `${API}/email-lab/scheduled-jobs/${job.id}/test-send`,
        { to_email: email, variant: testVariant },
      );
      toast.success(`Test email sent to ${res.data.to} (Variant ${res.data.variant})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setTestSending(false);
    }
  };

  const save = async () => {
    if (!subjectA.trim() || !subjectB.trim()) {
      toast.error('Both variant subjects are required');
      return;
    }
    let scheduledIso = null;
    if (scheduledAt) {
      const d = new Date(scheduledAt);
      if (isNaN(d.getTime())) { toast.error('Invalid datetime'); return; }
      if (d.getTime() < Date.now() + 60_000) { toast.error('scheduled_at must be ≥1 minute in the future'); return; }
      scheduledIso = d.toISOString();
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/email-lab/scheduled-jobs/${job.id}`, {
        variant_a_subject: subjectA,
        variant_a_cta_label: ctaA || null,
        variant_b_subject: subjectB,
        variant_b_cta_label: ctaB || null,
        scheduled_at: scheduledIso,
        test_percent: Number(testPct),
        wait_minutes: Number(waitMin),
        resend_to_non_openers: resendOn,
        resend_wait_hours: Number(resendHrs),
        resend_subject: resendSubj.trim() || null,
      });
      toast.success('Scheduled campaign updated');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="scheduled-edit-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-zinc-50 sticky top-0">
          <div className="flex items-center gap-2">
            <Edit3 className="w-4 h-4 text-zinc-500" />
            <h3 className="font-black text-sm uppercase tracking-wider">Edit scheduled campaign</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-2xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Send at</label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              data-testid="scheduled-edit-datetime"
              className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none"
            />
          </div>

          <div className="border border-blue-200 bg-blue-50/40 p-3 space-y-3">
            <div className="text-[10px] font-black uppercase tracking-wider text-blue-700">Variant A</div>
            <input type="text" value={subjectA} onChange={(e) => setSubjectA(e.target.value)}
                   placeholder="Subject A" data-testid="scheduled-edit-subject-a"
                   className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none" />
            <input type="text" value={ctaA} onChange={(e) => setCtaA(e.target.value)}
                   placeholder="CTA A label" data-testid="scheduled-edit-cta-a"
                   className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none" />
          </div>

          <div className="border border-orange-200 bg-orange-50/40 p-3 space-y-3">
            <div className="text-[10px] font-black uppercase tracking-wider text-orange-700">Variant B</div>
            <input type="text" value={subjectB} onChange={(e) => setSubjectB(e.target.value)}
                   placeholder="Subject B" data-testid="scheduled-edit-subject-b"
                   className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none" />
            <input type="text" value={ctaB} onChange={(e) => setCtaB(e.target.value)}
                   placeholder="CTA B label" data-testid="scheduled-edit-cta-b"
                   className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <NumberField label="Test pool %"   value={testPct} min={5} max={50}   onChange={setTestPct} testId="scheduled-edit-testpct" />
            <NumberField label="Wait minutes"  value={waitMin} min={5} max={1440} onChange={setWaitMin} testId="scheduled-edit-waitmin" />
          </div>

          <div className="border border-zinc-200 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Auto-resend to non-openers</div>
              <ToggleButton on={resendOn} onToggle={() => setResendOn((v) => !v)} testId="scheduled-edit-resend" />
            </div>
            {resendOn && (
              <>
                <NumberField label="Resend wait hours" value={resendHrs} min={1} max={336} onChange={setResendHrs} testId="scheduled-edit-resend-hrs" />
                <input type="text" value={resendSubj} onChange={(e) => setResendSubj(e.target.value)}
                       placeholder="⚡ Still available — <original>"
                       data-testid="scheduled-edit-resend-subject"
                       className="w-full px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none" />
              </>
            )}
          </div>

          <div className="border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
            <div className="flex items-center gap-2">
              <Send className="w-3.5 h-3.5 text-emerald-700" />
              <div className="text-[10px] font-black uppercase tracking-wider text-emerald-700">Test send to inbox</div>
            </div>
            <p className="text-[11px] text-emerald-900/80 leading-snug">
              Fires a one-off test email using the current subject + CTA and today&apos;s live loads. Not tracked in analytics.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="scheduled-edit-test-email"
                className="flex-1 px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none"
              />
              <select
                value={testVariant}
                onChange={(e) => setTestVariant(e.target.value)}
                data-testid="scheduled-edit-test-variant"
                className="px-2 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none bg-white"
              >
                <option value="A">Variant A</option>
                <option value="B">Variant B</option>
              </select>
            </div>
            <button
              onClick={sendTest}
              disabled={testSending}
              data-testid="scheduled-edit-test-send"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 text-xs font-black uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {testSending ? (<><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending…</>) : (<><Send className="w-3.5 h-3.5" /> Send test to me</>)}
            </button>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 border border-zinc-300 text-zinc-700 hover:border-zinc-500 py-2.5 text-xs font-black uppercase tracking-wider">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="scheduled-edit-save"
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 text-xs font-black uppercase tracking-wider disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};



// ===================================================================
// Audit Log Panel — admin-only view of who sent what & when
// ===================================================================

const ACTION_LABELS = {
  send_digest_ab:            'A/B Digest Sent',
  send_digest_auto_winner:   'Auto-Winner A/B',
  send_digest:               'Manual Digest',
  notify_carriers:           'Load Notification',
  ab_winner_now:             'Winner-Send Triggered',
  scheduled_job_edit:        'Scheduled Job Edited',
  scheduled_job_cancel:      'Scheduled Job Cancelled',
  scheduled_job_test_send:   'Test Send',
  optimizer_config_update:   'Optimizer Settings Changed',
  optimizer_plan_now:        'Optimizer Plan-Now',
  suggest_subjects:          'AI Subject Suggest',
  digest_settings_update:    'Digest Settings Changed',
  digest_send_now:           'Digest Send-Now',
};

const AuditLogPanel = () => {
  const [events, setEvents]           = useState([]);
  const [actions, setActions]         = useState([]);
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter]   = useState('');
  const [days, setDays]                 = useState(30);
  const [loading, setLoading]           = useState(false);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200, days };
      if (actionFilter) params.action = actionFilter;
      if (actorFilter.trim()) params.actor = actorFilter.trim();
      const res = await axios.get(`${API}/audit/email-sends`, { params });
      setEvents(res.data.events || []);
      setActions(res.data.actions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [days, actionFilter, actorFilter]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const fmtTs = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ts; }
  };

  return (
    <div className="space-y-4" data-testid="audit-log-panel">
      <div className="bg-white border border-zinc-200 p-4 sm:p-6 space-y-4">
        <div>
          <h3 className="text-lg font-black text-zinc-900 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-red-600" /> Marketing Email Audit Log
          </h3>
          <p className="text-xs text-zinc-500 mt-1">Every campaign send, digest change, and scheduled-job edit is logged here. Admin-only.</p>
        </div>

        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Action</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              data-testid="audit-filter-action"
              className="px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none bg-white min-w-[200px]"
            >
              <option value="">All actions</option>
              {actions.map((a) => (
                <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Actor (email contains)</label>
            <input
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              placeholder="jane@..."
              data-testid="audit-filter-actor"
              className="px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none min-w-[200px]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Last</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="audit-filter-days"
              className="px-3 py-2 text-sm border border-zinc-300 focus:border-zinc-900 outline-none bg-white"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={365}>1 year</option>
            </select>
          </div>
          <button
            onClick={fetchLog}
            disabled={loading}
            data-testid="audit-refresh"
            className="bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 text-xs font-black uppercase tracking-wider disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>

        {events.length === 0 && !loading && (
          <div className="text-center py-12 text-zinc-400 text-sm" data-testid="audit-empty">
            No events match your filters.
          </div>
        )}

        {events.length > 0 && (
          <div className="overflow-x-auto -mx-4 sm:-mx-6" data-testid="audit-events-list">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-zinc-50 border-y border-zinc-200">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] font-black uppercase tracking-wider text-zinc-500">When</th>
                  <th className="text-left px-4 py-2 text-[10px] font-black uppercase tracking-wider text-zinc-500">Who</th>
                  <th className="text-left px-4 py-2 text-[10px] font-black uppercase tracking-wider text-zinc-500">Action</th>
                  <th className="text-left px-4 py-2 text-[10px] font-black uppercase tracking-wider text-zinc-500">Subject / Resource</th>
                  <th className="text-right px-4 py-2 text-[10px] font-black uppercase tracking-wider text-zinc-500">Sent to</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {events.map((ev) => (
                  <tr key={ev.id} className="hover:bg-zinc-50" data-testid={`audit-row-${ev.id}`}>
                    <td className="px-4 py-2 text-xs text-zinc-600 whitespace-nowrap">{fmtTs(ev.timestamp)}</td>
                    <td className="px-4 py-2">
                      <div className="text-xs font-bold text-zinc-900">{ev.actor_name || ev.actor_email}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wide">{ev.actor_role}</div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-block bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider">
                        {ACTION_LABELS[ev.action] || ev.action}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-zinc-700 max-w-md">
                      <div className="truncate" title={ev.subject || ev.resource_id || ''}>
                        {ev.subject || <span className="text-zinc-400">{ev.resource_id || '—'}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-right text-zinc-900 font-bold whitespace-nowrap">
                      {ev.recipient_count != null ? ev.recipient_count.toLocaleString() : <span className="text-zinc-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
